import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
    Award, Download,
    RefreshCw, ChevronDown, User, Calendar, Check, X,
    FileSpreadsheet, AlertCircle, Inbox, CalendarDays, CalendarRange
} from 'lucide-react';
import {
    fetchMonthlyAchievementForm,
    fetchQuarterlyAchievementForm,
    fetchSalesOptions,
} from './achievementFormApi';
import { useAuth } from '../../../contexts/AuthProvider';
import { AppSelect, FormFieldSelect } from '../../../components/common/select';

// ── Constants ─────────────────────────────────────────────────────────────────

const TEAL = { dark: '#0d3d42', primary: '#1f656c', mid: '#2a8a93' };

// Excel-exact column group colors
const XL = {
    blue:  { bg: '#83CBEB', text: '#1a1a1a' },  // Invoice Date → Op.Exp
    peach: { bg: '#FFCC99', text: '#1a1a1a' },  // Revenue, Achieved
    green: { bg: '#8ED973', text: '#1a1a1a' },  // Incentive values
};

const MONTHS = [
    { value: 'all', label: 'All Months' },
    { value: 1,  label: 'January' },  { value: 2,  label: 'February' },
    { value: 3,  label: 'March' },    { value: 4,  label: 'April' },
    { value: 5,  label: 'May' },      { value: 6,  label: 'June' },
    { value: 7,  label: 'July' },     { value: 8,  label: 'August' },
    { value: 9,  label: 'September' },{ value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const QUARTERS = [
    { value: 1, label: 'Q1', sub: 'Jan – Mar' },
    { value: 2, label: 'Q2', sub: 'Apr – Jun' },
    { value: 3, label: 'Q3', sub: 'Jul – Sep' },
    { value: 4, label: 'Q4', sub: 'Oct – Dec' },
];

const currentYear  = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtRp = (val) => {
    if (val == null || val === '') return '-';
    const n = Number(val);
    if (isNaN(n)) return '-';
    return 'Rp ' + new Intl.NumberFormat('id-ID').format(n);
};

const fmtNum = (val) => {
    if (val == null || val === '') return '-';
    const n = Number(val);
    if (isNaN(n)) return '-';
    return new Intl.NumberFormat('id-ID').format(n);
};

// ── Sub-components ────────────────────────────────────────────────────────────

const AchieveBadge = ({ status, size = 'sm' }) => {
    const ok = status === 'Achieved';
    return (
        <span className={`badge d-inline-flex align-items-center ${ok ? 'text-white' : 'text-white'}`}
              style={{
                  fontSize: size === 'lg' ? '0.9rem' : '0.72rem',
                  padding: size === 'lg' ? '6px 14px' : '3px 9px',
                  borderRadius: 999,
                  background: ok ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #ef4444, #dc2626)',
                  boxShadow: size === 'lg' ? '0 2px 6px rgba(0,0,0,0.12)' : 'none',
              }}>
            {ok ? <><Check size={10} className="me-1" />Achieved</> : <><X size={10} className="me-1" />Not Achieved</>}
        </span>
    );
};

const SkeletonRow = ({ cols }) => (
    <tr>
        {Array.from({ length: cols }).map((_, i) => (
            <td key={i}><div className="placeholder-glow"><span className="placeholder col-12 rounded" /></div></td>
        ))}
    </tr>
);

const EmptyState = ({ colSpan }) => (
    <tr>
        <td colSpan={colSpan}>
            <div className="text-center py-5 text-muted">
                <Inbox size={40} className="mb-2 opacity-50" />
                <div className="fw-semibold">Tidak ada data</div>
                <div style={{ fontSize: '0.8rem' }}>Tidak ada invoice untuk periode dan sales yang dipilih.</div>
            </div>
        </td>
    </tr>
);

// ── Main Component ────────────────────────────────────────────────────────────

export default function AchievementForm() {
    const { user, hasRole, can } = useAuth();
    // Mirrors ModuleRoute's admin/director bypass; everyone else needs the
    // "achievement" module's View (Global) capability (Set Permissions) to
    // see all sales — otherwise they're locked to their own data below.
    const canSeeAllSales = hasRole('ROLE_ADMIN') || hasRole('ROLE_DIRECTOR') || can('view_global', 'achievement');

    // ── Filter state ─────────────────────────────────────────────────────────
    const [salesOptions, setSalesOptions]       = useState([]);
    const [selectedSalesId, setSelectedSalesId] = useState('');
    const [selectedYear, setSelectedYear]       = useState(currentYear);
    const [selectedMonth, setSelectedMonth]     = useState(new Date().getMonth() + 1);
    const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
    const [activeTab, setActiveTab]             = useState('monthly');

    // ── Data state ───────────────────────────────────────────────────────────
    const [monthlyData, setMonthlyData]   = useState(null);
    const [quarterlyData, setQuarterlyData] = useState(null);
    const [loading, setLoading]           = useState(false);
    const [error, setError]               = useState('');

    // ── Init: load sales list + set default salesId ───────────────────────────
    useEffect(() => {
        if (canSeeAllSales) {
            fetchSalesOptions()
                .then(opts => {
                    setSalesOptions(opts);
                    if (!selectedSalesId && opts.length > 0) {
                        setSelectedSalesId(String(opts[0].id));
                    }
                })
                .catch(() => {});
        } else {
            // Non-admin: locked to own user
            if (user?.id) setSelectedSalesId(String(user.id));
        }
    // eslint-disable-next-line
    }, [canSeeAllSales]);

    // ── Load data ─────────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        if (!selectedSalesId) return;
        setLoading(true);
        setError('');
        try {
            if (activeTab === 'monthly') {
                const monthParam = selectedMonth === 'all' ? undefined : selectedMonth;
                const data = await fetchMonthlyAchievementForm(selectedSalesId, selectedYear, monthParam);
                setMonthlyData(data);
            } else {
                const data = await fetchQuarterlyAchievementForm(selectedSalesId, selectedYear, selectedQuarter);
                setQuarterlyData(data);
            }
        } catch {
            setError('Gagal memuat data. Periksa koneksi atau coba lagi.');
        } finally {
            setLoading(false);
        }
    }, [selectedSalesId, selectedYear, selectedMonth, selectedQuarter, activeTab]);

    useEffect(() => { loadData(); }, [loadData]);

    const currentData = activeTab === 'monthly' ? monthlyData : quarterlyData;

    // ── Export Excel ──────────────────────────────────────────────────────────
    const buildExcelRows = (rows, isQuarterly = false) =>
        (rows || []).map(r => [
            r.invoiceDate, r.transactionType, r.invoiceNumber,
            r.contractDate, r.contractNumber, r.period, r.picName, r.serviceType,
            r.clientName, r.consultantName, r.productName, r.productCategory, r.memo,
            r.amountInvoice, r.cmf, r.vat, r.totalInvoice, r.operationExpenditures,
            r.revenue,
            ...(isQuarterly ? [] : [r.incentiveRevenue, r.incentivePushProduct, r.totalIncentive]),
        ]);

    const MONTHLY_HEADERS = [
        'Invoice Date','Tx Type','Invoice No','Contract Date','Contract No','Period','PIC',
        'Service Type','Client','Consultant','Product','Category','Memo',
        'Amount Invoice','CMF','VAT','Total Invoice','Op. Expenditures','Revenue',
        'Incentive Revenue','Incentive Push Product','Total Incentive',
    ];
    const QUARTERLY_HEADERS = [
        'Invoice Date','Tx Type','Invoice No','Contract Date','Contract No','Period','PIC',
        'Service Type','Client','Consultant','Product','Category','Memo',
        'Amount Invoice','CMF','VAT','Total Invoice','Op. Expenditures','Revenue',
    ];

    const exportExcel = () => {
        const wb = XLSX.utils.book_new();

        if (activeTab === 'monthly' && monthlyData) {
            const d = monthlyData;
            const totalRow = [
                '','','','','','','','','','','','','TOTAL',
                d.totalAmountInvoice, d.totalCmf, d.totalVat, d.totalInvoice,
                d.totalOperationExpenditures, d.totalRevenue,
                d.totalIncentiveRevenue, d.totalIncentivePushProduct, d.grandTotalIncentive,
            ];
            const ws = XLSX.utils.aoa_to_sheet([
                ['PT CBQA GLOBAL INDONESIA'],
                ['Monthly Achievement Form'],
                ['Nama Sales', ':', d.salesName],
                ['Department', ':', d.department],
                ['Period',     ':', d.periodLabel],
                [],
                MONTHLY_HEADERS,
                ...buildExcelRows(d.rows, false),
                totalRow,
                [],
                ['Prepare by,', '', 'Checked by,', '', 'Approved by,', '', 'Review by,', 'Calculate by,'],
                ['', '', '(N+1)', '', '(N+2)'],
                [d.salesName, '', '', '', '', '', 'HR Manager', 'Finance'],
            ]);
            XLSX.utils.book_append_sheet(wb, ws, 'Monthly Achievement Form');
            XLSX.writeFile(wb, `Achievement_Monthly_${d.salesName}_${d.periodLabel}.xlsx`);
        } else if (activeTab === 'quarterly' && quarterlyData) {
            const d = quarterlyData;
            const totalRow = [
                '','','','','','','','','','','','',`TOTAL ${d.periodLabel}`,
                d.totalAmountInvoice, d.totalCmf, d.totalVat, d.totalInvoice,
                d.totalOperationExpenditures, d.totalRevenue,
            ];
            const ws = XLSX.utils.aoa_to_sheet([
                ['PT CBQA GLOBAL INDONESIA'],
                ['Quarterly Achievement Form'],
                ['Nama Sales', ':', d.salesName],
                ['Department', ':', d.department],
                ['Period',     ':', d.periodLabel],
                [],
                QUARTERLY_HEADERS,
                ...buildExcelRows(d.rows, true),
                totalRow,
                [],
                ['Prepare by,', '', 'Checked by,', '', 'Approved by,', '', 'Review by,', 'Calculate by,'],
                ['', '', '(N+1)', '', '(N+2)'],
                [d.salesName, '', '', '', '', '', 'HR Manager', 'Finance'],
            ]);
            XLSX.utils.book_append_sheet(wb, ws, 'Quaterly Achievement Form');
            XLSX.writeFile(wb, `Achievement_Quarterly_${d.salesName}_${d.periodLabel}.xlsx`);
        }
    };

    // ── Render: filter bar ────────────────────────────────────────────────────
    const renderFilters = () => (
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
            <div className="card-body py-3">
                <div className="row g-3 align-items-end">
                    {/* Sales selector */}
                    <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                        <label className="form-label small fw-semibold mb-1">
                            <User size={13} className="me-1 opacity-60" />Sales
                        </label>
                        {canSeeAllSales ? (
                            <div className="app-select--sm">
                                <AppSelect
                                    name="salesId"
                                    value={selectedSalesId}
                                    onChange={(name, value) => setSelectedSalesId(value)}
                                    options={salesOptions}
                                    placeholder="Cari sales..."
                                    valueField="id"
                                    labelField="name"
                                />
                            </div>
                        ) : (
                            <input
                                className="form-control form-control-sm bg-light"
                                value={user?.fullName || user?.name || user?.username || 'Me'}
                                readOnly
                            />
                        )}
                    </div>

                    {/* Year */}
                    <div className="col-6 col-sm-3 col-md-2 col-lg-2">
                        <label className="form-label small fw-semibold mb-1">
                            <Calendar size={13} className="me-1 opacity-60" />Year
                        </label>
                        <FormFieldSelect className="app-select--sm"
                            value={selectedYear}
                            onChange={e => setSelectedYear(Number(e.target.value))}
                        >
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </FormFieldSelect>
                    </div>

                    {/* Month / Quarter */}
                    {activeTab === 'monthly' ? (
                        <div className="col-6 col-sm-3 col-md-3 col-lg-2">
                            <label className="form-label small fw-semibold mb-1">Month</label>
                            <FormFieldSelect className="app-select--sm"
                                value={selectedMonth}
                                onChange={e => setSelectedMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            >
                                {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </FormFieldSelect>
                        </div>
                    ) : (
                        <div className="col-6 col-sm-3 col-md-3 col-lg-2">
                            <label className="form-label small fw-semibold mb-1">Quarter</label>
                            <div className="d-flex gap-1">
                                {QUARTERS.map(q => (
                                    <button
                                        key={q.value}
                                        onClick={() => setSelectedQuarter(q.value)}
                                        className={`btn btn-sm flex-fill ${selectedQuarter === q.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={selectedQuarter === q.value ? { background: TEAL.primary, borderColor: TEAL.primary } : {}}
                                        title={q.sub}
                                    >
                                        {q.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="col-12 col-md-auto ms-md-auto d-flex gap-2">
                        <button
                            className="btn btn-sm btn-outline-secondary rounded-pill d-flex align-items-center gap-1 px-3"
                            onClick={loadData}
                            disabled={loading || !selectedSalesId}
                        >
                            <RefreshCw size={13} className={loading ? 'spin' : ''} />
                            Refresh
                        </button>
                        <button
                            className="btn btn-sm rounded-pill d-flex align-items-center gap-1 text-white px-3"
                            style={{ background: '#16a34a', borderColor: '#16a34a' }}
                            onClick={exportExcel}
                            disabled={!currentData}
                        >
                            <FileSpreadsheet size={13} />
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    // ── Render: table (monthly) ───────────────────────────────────────────────
    const renderMonthlyTable = () => {
        const d = monthlyData;
        if (!d) return null;
        const rows = d.rows || [];
        return (
            <div className="card border-0 shadow-sm" style={{ borderRadius: 12, overflow: 'hidden' }}>
                <div className="card-body p-0">
                    <div className="table-responsive" style={{ maxHeight: 520 }}>
                        <table className="table table-sm table-hover table-bordered mb-0 align-middle"
                               style={{ fontSize: '0.75rem', minWidth: 1820 }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                <tr>
                                    {/* Blue group: transaction data (col A–S in Excel) */}
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Invoice Date</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Transaction Type</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Invoice Number</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Contract Date</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Contract No</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Period</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>PIC</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Service Type</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Client Name</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Product Name</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Product Category</th>
                                    <th className="text-nowrap px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Memo</th>
                                    <th className="text-nowrap text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Amount Invoice</th>
                                    <th className="text-nowrap text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Certification<br/>Management Fee</th>
                                    <th className="text-nowrap text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>VAT</th>
                                    <th className="text-nowrap text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Total Invoice</th>
                                    <th className="text-nowrap text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Operation<br/>Expenditures</th>
                                    {/* Peach group: Revenue & Achievement (col T–U in Excel) */}
                                    <th className="text-nowrap text-end px-2 fw-bold" style={{ background: XL.peach.bg, color: XL.peach.text }}>Revenue<br/><span style={{ fontWeight: 400, fontSize: '0.68rem' }}>(Achievement)</span></th>
                                    <th className="text-nowrap text-center px-2" style={{ background: XL.peach.bg, color: XL.peach.text }}>Achieved /<br/>Not Achieved</th>
                                    {/* Green group: Incentive Value Estimate (col W–X in Excel) */}
                                    <th className="text-nowrap text-end px-2" style={{ background: XL.green.bg, color: XL.green.text }}>Incentive Value<br/><span style={{ fontWeight: 400, fontSize: '0.68rem' }}>Amount Revenue</span></th>
                                    <th className="text-nowrap text-end px-2" style={{ background: XL.green.bg, color: XL.green.text }}>Incentive Value<br/><span style={{ fontWeight: 400, fontSize: '0.68rem' }}>Amount Push Product</span></th>
                                    <th className="text-nowrap text-end px-2 fw-bold" style={{ background: XL.green.bg, color: XL.green.text }}>Total Incentive<br/>Estimate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading
                                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={22} />)
                                    : rows.length === 0
                                        ? <EmptyState colSpan={22} />
                                        : rows.map((row, i) => {
                                            return (
                                                <tr key={row.invoiceId ?? i}
                                                    style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                    <td className="px-2 text-nowrap">{row.invoiceDate}</td>
                                                    <td className="px-2">
                                                        <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>
                                                            {row.transactionType}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 text-nowrap" style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                                        {row.invoiceNumber}
                                                    </td>
                                                    <td className="px-2 text-nowrap">{row.contractDate}</td>
                                                    <td className="px-2 text-nowrap" style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                                        {row.contractNumber}
                                                    </td>
                                                    <td className="px-2">{row.period}</td>
                                                    <td className="px-2 text-nowrap">{row.picName}</td>
                                                    <td className="px-2">{row.serviceType}</td>
                                                    <td className="px-2" style={{ maxWidth: 140 }}>
                                                        <span className="d-block text-truncate" style={{ maxWidth: 130 }} title={row.clientName}>
                                                            {row.clientName}
                                                        </span>
                                                    </td>
                                                    <td className="px-2" style={{ maxWidth: 160 }}>
                                                        <span className="d-block text-truncate" style={{ maxWidth: 150 }} title={row.productName}>
                                                            {row.productName}
                                                        </span>
                                                    </td>
                                                    <td className="px-2">
                                                        <span className={`badge ${row.isPushProduct ? 'bg-warning text-dark' : 'bg-light text-dark border'}`}
                                                              style={{ fontSize: '0.67rem' }}>
                                                            {row.isPushProduct ? '★ Push' : 'Non Push'}
                                                        </span>
                                                    </td>
                                                    <td className="px-2" style={{ maxWidth: 120 }}>
                                                        <span className="d-block text-truncate" style={{ maxWidth: 110 }} title={row.memo}>
                                                            {row.memo || '-'}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 text-end">{fmtNum(row.amountInvoice)}</td>
                                                    <td className="px-2 text-end">{fmtNum(row.cmf)}</td>
                                                    <td className="px-2 text-end">{fmtNum(row.vat)}</td>
                                                    <td className="px-2 text-end">{fmtNum(row.totalInvoice)}</td>
                                                    <td className="px-2 text-end">{fmtNum(row.operationExpenditures)}</td>
                                                    {/* Peach cells */}
                                                    <td className="px-2 text-end fw-semibold" style={{ background: '#fff9f0' }}>
                                                        {fmtNum(row.revenue)}
                                                    </td>
                                                    <td className="px-2 text-center" style={{ background: '#fff9f0' }}>
                                                        <AchieveBadge status={row.achievedStatus} />
                                                    </td>
                                                    {/* Green cells */}
                                                    <td className="px-2 text-end" style={{ background: '#f4fff0' }}>{fmtNum(row.incentiveRevenue)}</td>
                                                    <td className="px-2 text-end" style={{ background: '#f4fff0' }}>{fmtNum(row.incentivePushProduct)}</td>
                                                    <td className="px-2 text-end fw-bold" style={{ background: '#f4fff0' }}>{fmtNum(row.totalIncentive)}</td>
                                                </tr>
                                            );
                                        })
                                }
                            </tbody>
                            {!loading && monthlyData && (
                                <tfoot>
                                    <tr style={{ fontWeight: 700, fontSize: '0.78rem' }}>
                                        <td colSpan={12} className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>TOTAL</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(monthlyData.totalAmountInvoice)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(monthlyData.totalCmf)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(monthlyData.totalVat)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(monthlyData.totalInvoice)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(monthlyData.totalOperationExpenditures)}</td>
                                        <td className="text-end px-2" style={{ background: XL.peach.bg, color: XL.peach.text }}>{fmtNum(monthlyData.totalRevenue)}</td>
                                        <td className="text-center px-2" style={{ background: XL.peach.bg, color: XL.peach.text }}>
                                            <AchieveBadge status={monthlyData.achievedStatus} size="lg" />
                                        </td>
                                        <td className="text-end px-2" style={{ background: XL.green.bg, color: XL.green.text }}>{fmtNum(monthlyData.totalIncentiveRevenue)}</td>
                                        <td className="text-end px-2" style={{ background: XL.green.bg, color: XL.green.text }}>{fmtNum(monthlyData.totalIncentivePushProduct)}</td>
                                        <td className="text-end px-2" style={{ background: XL.green.bg, color: XL.green.text }}>{fmtRp(monthlyData.grandTotalIncentive)}</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // ── Render: table (quarterly) ─────────────────────────────────────────────
    const renderQuarterlyTable = () => {
        const d = quarterlyData;
        if (!d) return null;
        const rows = d.rows || [];
        return (
            <div className="card border-0 shadow-sm" style={{ borderRadius: 12, overflow: 'hidden' }}>
                <div className="card-body p-0">
                    <div className="table-responsive" style={{ maxHeight: 520 }}>
                        <table className="table table-sm table-hover table-bordered mb-0 align-middle"
                               style={{ fontSize: '0.75rem', minWidth: 1700 }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                <tr>
                                    {/* Blue group: A–S in Excel */}
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Invoice Date</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Transaction Type</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Invoice Number</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Contract Date</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Contract No</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Period</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>PIC</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Service Type</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Client Name</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Product Name</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Product Category</th>
                                    <th className="px-2 text-center" style={{ background: XL.blue.bg, color: XL.blue.text }}>Memo</th>
                                    <th className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Amount Invoice</th>
                                    <th className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Certification<br/>Management Fee</th>
                                    <th className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>VAT</th>
                                    <th className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Total Invoice</th>
                                    <th className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>Operation<br/>Expenditures</th>
                                    {/* Peach group: T–U in Excel */}
                                    <th className="text-end px-2 fw-bold" style={{ background: XL.peach.bg, color: XL.peach.text }}>Revenue<br/><span style={{ fontWeight: 400, fontSize: '0.68rem' }}>(Achievement)</span></th>
                                    <th className="text-center px-2" style={{ background: XL.peach.bg, color: XL.peach.text }}>Achieved /<br/>Not Achieved</th>
                                    {/* Green group: V in Excel (Quarterly Incentive) */}
                                    <th className="text-end px-2 fw-bold" style={{ background: XL.green.bg, color: XL.green.text }}>Quarterly Incentive<br/><span style={{ fontWeight: 400, fontSize: '0.68rem' }}>({quarterlyData?.quarterlyIncentiveRate ?? 0.5}%)</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading
                                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} cols={19} />)
                                    : rows.length === 0
                                        ? <EmptyState colSpan={19} />
                                        : rows.map((row, i) => (
                                            <tr key={row.invoiceId ?? i}
                                                style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                <td className="px-2 text-nowrap">{row.invoiceDate}</td>
                                                <td className="px-2">
                                                    <span className="badge bg-secondary" style={{ fontSize: '0.68rem' }}>
                                                        {row.transactionType}
                                                    </span>
                                                </td>
                                                <td className="px-2 text-nowrap" style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                                    {row.invoiceNumber}
                                                </td>
                                                <td className="px-2 text-nowrap">{row.contractDate}</td>
                                                <td className="px-2 text-nowrap" style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                                    {row.contractNumber}
                                                </td>
                                                <td className="px-2">{row.period}</td>
                                                <td className="px-2 text-nowrap">{row.picName}</td>
                                                <td className="px-2">{row.serviceType}</td>
                                                <td className="px-2" style={{ maxWidth: 140 }}>
                                                    <span className="d-block text-truncate" style={{ maxWidth: 130 }} title={row.clientName}>
                                                        {row.clientName}
                                                    </span>
                                                </td>
                                                <td className="px-2" style={{ maxWidth: 160 }}>
                                                    <span className="d-block text-truncate" style={{ maxWidth: 150 }} title={row.productName}>
                                                        {row.productName}
                                                    </span>
                                                </td>
                                                <td className="px-2">
                                                    <span className={`badge ${row.isPushProduct ? 'bg-warning text-dark' : 'bg-light text-dark border'}`}
                                                          style={{ fontSize: '0.67rem' }}>
                                                        {row.isPushProduct ? '★ Push' : 'Non Push'}
                                                    </span>
                                                </td>
                                                <td className="px-2" style={{ maxWidth: 120 }}>
                                                    <span className="d-block text-truncate" style={{ maxWidth: 110 }} title={row.memo}>
                                                        {row.memo || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-2 text-end">{fmtNum(row.amountInvoice)}</td>
                                                <td className="px-2 text-end">{fmtNum(row.cmf)}</td>
                                                <td className="px-2 text-end">{fmtNum(row.vat)}</td>
                                                <td className="px-2 text-end">{fmtNum(row.totalInvoice)}</td>
                                                <td className="px-2 text-end">{fmtNum(row.operationExpenditures)}</td>
                                                {/* Peach cells */}
                                                <td className="px-2 text-end fw-semibold" style={{ background: '#fff9f0' }}>{fmtNum(row.revenue)}</td>
                                                <td className="px-2 text-center" style={{ background: '#fff9f0' }}>
                                                    <AchieveBadge status={row.achievedStatus} />
                                                </td>
                                                {/* Green cell — quarterly incentive per row (empty, shown only in footer) */}
                                                <td className="px-2 text-end" style={{ background: '#f4fff0' }}>-</td>
                                            </tr>
                                        ))
                                }
                            </tbody>
                            {!loading && quarterlyData && (
                                <tfoot>
                                    {/* Total row */}
                                    <tr style={{ fontWeight: 700, fontSize: '0.78rem' }}>
                                        <td colSpan={12} className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>
                                            TOTAL {quarterlyData.periodLabel?.toUpperCase()}
                                        </td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(quarterlyData.totalAmountInvoice)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(quarterlyData.totalCmf)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(quarterlyData.totalVat)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(quarterlyData.totalInvoice)}</td>
                                        <td className="text-end px-2" style={{ background: XL.blue.bg, color: XL.blue.text }}>{fmtNum(quarterlyData.totalOperationExpenditures)}</td>
                                        <td className="text-end px-2" style={{ background: XL.peach.bg, color: XL.peach.text }}>{fmtNum(quarterlyData.totalRevenue)}</td>
                                        <td className="text-center px-2" style={{ background: XL.peach.bg, color: XL.peach.text }}>
                                            <AchieveBadge status={quarterlyData.achievedStatus} size="lg" />
                                        </td>
                                        <td className="text-end px-2" style={{ background: XL.green.bg, color: XL.green.text }}>-</td>
                                    </tr>
                                    {/* Quarterly incentive summary row */}
                                    <tr style={{ fontWeight: 700, fontSize: '0.82rem' }}>
                                        <td colSpan={17} className="text-end px-2" style={{ background: XL.green.bg, color: '#1a1a1a' }}>
                                            Quarterly Incentive ({quarterlyData.quarterlyIncentiveRate}%) —&nbsp;
                                            <AchieveBadge status={quarterlyData.achievedStatus} />
                                        </td>
                                        <td className="text-end px-2" style={{ background: XL.green.bg, color: '#1a1a1a' }}>
                                            {fmtRp(quarterlyData.quarterlyIncentive)}
                                        </td>
                                        <td className="text-end px-2 fw-bold" style={{ background: XL.green.bg, color: '#1a1a1a', fontSize: '0.95rem' }}>
                                            {fmtRp(quarterlyData.quarterlyIncentive)}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // ── Signature section ─────────────────────────────────────────────────────
    const renderSignature = (salesName) => (
        <div className="card border-0 shadow-sm mt-4" style={{ borderRadius: 12, overflow: 'hidden' }}>
            <div className="card-header py-2 border-0"
                 style={{ background: `linear-gradient(135deg, ${TEAL.dark}, ${TEAL.primary})`, color: '#fff' }}>
                <span className="small fw-semibold">Signatures</span>
            </div>
            <div className="card-body">
                <div className="row text-center">
                    {[
                        { role: 'Prepare by,',  name: salesName },
                        { role: 'Checked by,',  name: '' },
                        { role: 'Approved by,', name: '' },
                        { role: 'Review by,',   name: 'HR Manager' },
                        { role: 'Calculate by,',name: 'Finance' },
                    ].map((sig, i) => (
                        <div key={i} className="col" style={{ minWidth: 120 }}>
                            <div className="small fw-semibold text-muted">{sig.role}</div>
                            <div style={{ marginTop: 52, borderTop: '1px solid #ccc', paddingTop: 6, fontSize: '0.82rem' }}>
                                {sig.name || <span className="text-muted">&nbsp;</span>}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // ── Main render ───────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
                .spin { animation: spin 0.8s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }
                .achv-tab-btn { transition: background 0.15s ease, color 0.15s ease; }
            `}</style>

            {/* Hero header */}
            <div className="mb-4 position-relative" style={{
                background: `linear-gradient(135deg, ${TEAL.dark} 0%, ${TEAL.primary} 60%, ${TEAL.mid} 100%)`,
                borderRadius: 14,
                padding: '26px 28px',
                color: '#fff',
                overflow: 'hidden',
                boxShadow: '0 10px 24px rgba(13,61,66,0.18)',
            }}>
                <Award size={140} className="position-absolute" style={{ right: -20, top: -30, opacity: 0.08 }} />
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 position-relative">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <div className="d-flex align-items-center justify-content-center rounded-circle"
                                 style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.15)' }}>
                                <Award size={18} />
                            </div>
                            <h4 className="mb-0 fw-bold">Achievement &amp; Incentive Form</h4>
                        </div>
                        <div style={{ fontSize: '0.85rem', opacity: 0.85 }}>
                            Monthly &amp; Quarterly achievement report per sales — PT CBQA Global Indonesia
                        </div>
                    </div>
                    {currentData && (
                        <div className="text-end px-3 py-2" style={{ background: 'rgba(255,255,255,0.12)', borderRadius: 10 }}>
                            <div style={{ fontSize: '0.78rem', opacity: 0.8 }}>
                                {currentData.periodLabel}
                            </div>
                            <div className="fw-bold" style={{ fontSize: '1rem' }}>
                                {currentData.salesName}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="d-inline-flex p-1 mb-3 gap-1" style={{ background: '#eef2f3', borderRadius: 999 }}>
                {[
                    { key: 'monthly', label: 'Monthly Achievement', icon: CalendarDays },
                    { key: 'quarterly', label: 'Quarterly Achievement', icon: CalendarRange },
                ].map(t => (
                    <button
                        key={t.key}
                        className="achv-tab-btn btn btn-sm d-flex align-items-center gap-2 border-0 px-3"
                        style={{
                            borderRadius: 999,
                            background: activeTab === t.key ? TEAL.primary : 'transparent',
                            color: activeTab === t.key ? '#fff' : '#495057',
                            fontWeight: activeTab === t.key ? 600 : 500,
                        }}
                        onClick={() => setActiveTab(t.key)}
                    >
                        <t.icon size={14} />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Filter card */}
            {renderFilters()}

            {/* Error */}
            {error && (
                <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3">
                    <AlertCircle size={16} />
                    <span className="small">{error}</span>
                </div>
            )}

            {/* Table */}
            {activeTab === 'monthly' && renderMonthlyTable()}
            {activeTab === 'quarterly' && renderQuarterlyTable()}

            {/* Signature */}
            {currentData && !loading && (
                renderSignature(currentData.salesName)
            )}
        </>
    );
}
